import cloneDeep from "lodash.clonedeep";
import { parse, stringify } from "qs";

import { Component, Vue, Watch } from "vue-property-decorator";

import CardHeader from "@/components/Includes/CardHeader/CardHeader.vue";
import Loader from "@/components/Includes/Loader/Loader";
import IStatisticsReport from "@/interfaces/IStatisticsReport";
import checkEmail from "@/libs/checkEmail";
import isEmpty from "@/libs/isEmpty";
import { format, parseISO } from "date-fns";
import { namespace } from "vuex-class";

const statisticsReportModule = namespace("statisticsReport");

@Component({
  components: {
    Loader,
    CardHeader,
  },
})
export default class StatisticsReport extends Vue {
  @statisticsReportModule.Action
  private readonly fetchStatisticsReport!: (params: {
    id: number;
  }) => Promise<IStatisticsReport>;

  @statisticsReportModule.Action
  private readonly updateStatisticsReport!: (
    params: IStatisticsReport
  ) => Promise<IStatisticsReport>;

  private isLoading = false;
  private report: null | IStatisticsReport = null;
  private form: null | IStatisticsReport = null;
  private email = "";

  private readonly orderTypes = [
    { value: "asc", text: "ASC" },
    { value: "desc", text: "DESC" },
  ];

  private readonly orderNames = [
    { value: "group", text: "Group" },
    { value: "clicks", text: "Clicks" },
    { value: "uniques", text: "Uniques" },
    { value: "installs", text: "Installs" },
    { value: "subs", text: "Subs" },
    { value: "trials", text: "trials" },
    { value: "converted", text: "Converted" },
    { value: "ICR", text: "ICR" },
    { value: "TCR", text: "TCR" },
    { value: "SCR", text: "SCR" },
    { value: "refund", text: "Refund" },
    { value: "abuse", text: "Abuse" },
    { value: "rebills", text: "Rebills" },
    { value: "costs", text: "Costs" },
    { value: "CPT", text: "CPT" },
    { value: "revenue", text: "Revenue" },
    { value: "refundSum", text: "RefundSum" },
    { value: "profit", text: "profit" },
    { value: "PPS", text: "PPS" },
    { value: "PPT", text: "PPT" },
    { value: "ROI", text: "ROI" },
  ];

  private get parsedFormGetParams() {
    if (this.form) {
      return parse(this.form.getParams);
    }
    return null;
  }

  private get orderName() {
    if (this.parsedFormGetParams) {
      const { order_name = "" } = this.parsedFormGetParams;

      return order_name as string;
    }
    return "";
  }

  private set orderName(val: string) {
    if (this.form && this.parsedFormGetParams) {
      this.form.getParams = stringify({
        ...this.parsedFormGetParams,
        order_name: val,
      });
    }
  }

  private get orderType() {
    if (this.parsedFormGetParams) {
      const { order_type = "" } = this.parsedFormGetParams;

      return order_type as string;
    }
    return "";
  }

  private set orderType(val: string) {
    if (this.form && this.parsedFormGetParams) {
      this.form.getParams = stringify({
        ...this.parsedFormGetParams,
        order_type: val,
      });
    }
  }

  private get isEdit() {
    return this.$route.query.mode === "edit";
  }

  private set isEdit(edit: boolean) {
    this.$router.replace({
      query: {
        ...this.$route.query,
        mode: edit ? "edit" : "",
      },
    });
  }

  private get btnIcon() {
    return !this.isEdit ? "icon-pencil" : "icon-lock";
  }

  private get editBtnText() {
    return !this.isEdit ? "Edit" : "Lock";
  }

  private get reportId() {
    return +this.$route.params.id;
  }

  private get btnText() {
    return !this.isEdit ? "Create" : "Update";
  }

  private get isEmptyEmail() {
    return isEmpty(this.email);
  }

  private get isValidEmail() {
    return checkEmail(this.email);
  }

  private deleteEmail(email: string) {
    if (this.form) {
      this.form.emails.splice(
        this.form.emails.findIndex((e) => e === email),
        1
      );
    }
  }

  private addEmail(e: KeyboardEvent) {
    const email = (e.target as HTMLInputElement).value;
    if (this.isValidEmail && !this.isEmptyEmail) {
      if (this.form && !this.form.emails.includes(email)) {
        this.form.emails.push(email);
      }

      this.email = "";
    }
  }

  private async sendForm() {
    if (this.form) {
      this.isLoading = true;
      try {
        await this.updateStatisticsReport(cloneDeep(this.form));
        this.isLoading = false;
        this.report = this.form;

        this.$notify({
          group: "notice",
          title: "Update",
          text: "Successfully",
          type: "success",
        });
      } catch {
        this.isLoading = false;
      }
    }
  }

  private cancelForm() {
    this.$router.push({name: 'statistics_reports'});
  }

  @Watch("report")
  private initForm() {
    if (this.report) {
      this.form = cloneDeep(this.report);
      this.email = '';
    }
  }

  private formatDate(date: string) {
    return format(parseISO(date), "dd.MM.yyyy HH:mm:ss");
  }

  private toggleForm() {
    this.isEdit = !this.isEdit;
    this.initForm();
  }

  private async created() {
    this.isLoading = true;

    try {
      this.report = await this.fetchStatisticsReport({ id: this.reportId });

      this.isLoading = false;
    } catch {
      this.isLoading = false;
    }
  }
}
